import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import type { TableSession, TableState } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  CardContent,
  Link,
  List,
  ListItem,
  Navbar,
  NavRight,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import {
  useCloseSession,
  useForceClose,
  useMergeSession,
  useMoveSession,
  useStaffRequestBill,
  useTableSession,
  useTableStates,
} from '../hooks/useTableOrdering';

/**
 * The bill - a calm overview that opens from the tab inspector and the head of
 * a stepped settle flow. It answers one question - what's owed and how far
 * along - and offers exactly one forward action for where the tab is: split,
 * take a payment, collect the split's shares, or close. The actual work each
 * implies happens on its own focused page (split / collect / payment). The tab
 * logistics that aren't part of settling - request bill, move, merge,
 * force-close - are tucked behind a "Manage" menu so they never compete with
 * the money path.
 */
export default function BillPage({ f7route }: { f7route: { params: { sessionId: string } } }) {
  const tAll = useT();
  const t = tAll.tableOrdering.bill;
  const sessionId = f7route.params.sessionId;
  const { data: session, isLoading } = useTableSession(sessionId);

  if (isLoading || !session) {
    return (
      <Page name="bill">
        <Navbar title={t.title} backLink={tAll.back} />
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      </Page>
    );
  }
  return <BillOverview session={session} />;
}

function BillOverview({ session }: { session: TableSession }) {
  const tAll = useT();
  const t = tAll.tableOrdering.bill;
  const tInspector = tAll.tableOrdering.inspector;
  const tSplit = tAll.tableOrdering.billSplit;
  const tPayment = tAll.tableOrdering.payment;
  const requestBill = useStaffRequestBill();
  const closeSession = useCloseSession();
  const forceClose = useForceClose();
  const moveSession = useMoveSession();
  const mergeSession = useMergeSession();
  const { data: tables } = useTableStates();

  const PAY_LABEL: Record<string, string> = {
    PENDING_COLLECTION: t.payStatus.pendingCollection,
    AWAITING_PROOF: t.payStatus.awaitingProof,
    PROOF_SUBMITTED: t.payStatus.proofSubmitted,
    PROCESSING: t.payStatus.processing,
    PAID: t.payStatus.paid,
    REJECTED: t.payStatus.rejected,
    VOID: t.payStatus.void,
    REFUNDED: t.payStatus.refunded,
    PARTIALLY_REFUNDED: t.payStatus.partiallyRefunded,
  };

  const SPLIT_LABEL: Record<string, string> = {
    EVEN: tSplit.even,
    BY_AMOUNT: tSplit.byAmount,
    BY_SEAT: tSplit.bySeat,
  };

  const currency = session.currency;
  const balance = Math.max(0, session.totalCents - session.paidCents);
  const settled = session.paidCents >= session.totalCents && session.totalCents > 0;
  const closed = session.status === 'CLOSED';
  // A split plan is the only thing that creates uncollected (PENDING_COLLECTION)
  // payments, so any pending row means we're mid-collection of a split.
  const pending = session.payments.filter((p) => p.status === 'PENDING_COLLECTION');
  const collectingSplit = pending.length > 0;
  const collectedCount = session.payments.filter((p) => p.status === 'PAID').length;
  const paidHistory = session.payments.filter((p) => p.status === 'PAID');
  // The one forward action this tab needs, as mutually-exclusive flags so each
  // Settle row can be a direct child of <List> (Rule 9 - no Fragment wrapper).
  const unsplitPartial = !settled && !collectingSplit && session.payments.length > 0;
  const fresh = !settled && !collectingSplit && session.payments.length === 0;
  const methodName = (k: string) => (k === 'CASH' ? tPayment.cash : tPayment.cardTerminal);
  // A bill is only settleable once every round is finished (served or
  // cancelled) - the backend refuses otherwise, so we surface the blocker here
  // rather than let a settle tap dead-end on an error toast.
  const openRounds = session.rounds.filter(
    (r) => r.status === 'PLACED' || r.status === 'FIRED' || r.status === 'READY',
  );
  const kitchenBusy = openRounds.length > 0;
  const openRoundsText = (() => {
    const seqs = openRounds.map((r) => r.sequence).sort((a, b) => a - b);
    return t.roundsNotServed(seqs.length, seqs.join(', '));
  })();
  // The receipt earns its place only when there's something to itemise beyond a
  // bare subtotal: a service charge that explains why total > subtotal, or a
  // part-payment to reconcile against. Otherwise subtotal === total and the
  // headline already says everything.
  const showReceipt = session.serviceChargeCents > 0 || session.paidCents > 0;

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });
  const go = (sub: string) =>
    f7.views.current.router.navigate(`/tables/session/${session.id}/bill/${sub}`);
  const back = () => f7.views.current.router.back();

  function doRequestBill() {
    requestBill.mutate(
      { sessionId: session.id, body: {} },
      { onSuccess: () => toast(t.markedBillRequested) },
    );
  }

  function doClose() {
    f7.dialog.confirm(t.closeConfirm, t.closeTitle, () => {
      closeSession.mutate(
        { sessionId: session.id },
        {
          onSuccess: () => {
            toast(t.tabClosed);
            back();
          },
        },
      );
    });
  }

  function doForceClose() {
    f7.dialog.prompt(t.forceClosePrompt, t.forceCloseTitle, (reason) => {
      if (!reason.trim()) return;
      forceClose.mutate(
        { sessionId: session.id, body: { reason: reason.trim() } },
        {
          onSuccess: () => {
            toast(t.forceCloseDone);
            back();
          },
        },
      );
    });
  }

  function doMove() {
    const free = (tables ?? []).filter(
      (tb: TableState) => tb.state === 'AVAILABLE' && tb.tableId !== session.tableId,
    );
    if (free.length === 0) return toast(t.noFreeTable);
    f7.dialog
      .create({
        title: t.moveTitle,
        buttons: [
          ...free.slice(0, 8).map((tb: TableState) => ({
            text: tb.label,
            onClick: () =>
              moveSession.mutate(
                { sessionId: session.id, body: { toTableId: tb.tableId } },
                { onSuccess: () => toast(t.movedTo(tb.label)) },
              ),
          })),
          { text: tAll.cancel, color: 'red' },
        ],
        verticalButtons: true,
      })
      .open();
  }

  function doMerge() {
    const others = (tables ?? []).filter(
      (tb: TableState) => tb.state === 'OCCUPIED' && tb.sessionId && tb.sessionId !== session.id,
    );
    if (others.length === 0) return toast(t.noOtherTab);
    f7.dialog
      .create({
        title: t.mergeTitle,
        text: t.mergeDesc,
        buttons: [
          ...others.slice(0, 8).map((tb: TableState) => ({
            text: tb.label,
            onClick: () =>
              mergeSession.mutate(
                { sessionId: session.id, body: { fromSessionId: tb.sessionId ?? '' } },
                { onSuccess: () => toast(t.mergedWith(tb.label)) },
              ),
          })),
          { text: tAll.cancel, color: 'red' },
        ],
        verticalButtons: true,
      })
      .open();
  }

  // Tab logistics, off the money path - opened from the navbar "Manage" menu.
  function openManage() {
    f7.actions
      .create({
        buttons: [
          [
            { text: t.manage, label: true },
            {
              text: t.viewActivity,
              onClick: () =>
                f7.views.current.router.navigate(`/tables/session/${session.id}/timeline`),
            },
            ...(session.status === 'OPEN'
              ? [{ text: t.markBillRequested, onClick: doRequestBill }]
              : []),
            // Paying closes the tab automatically; this is the safety net for a
            // tab that's settled but somehow didn't close (e.g. a failed close).
            ...(settled ? [{ text: t.closeTitle, onClick: doClose }] : []),
            { text: t.moveTabAnother, onClick: doMove },
            { text: t.mergeAnother, onClick: doMerge },
            { text: t.forceCloseWalkout, color: 'red', onClick: doForceClose },
          ],
          [{ text: tAll.cancel, color: 'red' }],
        ],
      })
      .open();
  }

  return (
    <Page name="bill">
      <Navbar title={t.billTitleFor(session.tableLabel)} backLink={tAll.back}>
        {!closed && (
          <NavRight>
            <Link iconF7="ellipsis_circle" onClick={openManage} />
          </NavRight>
        )}
      </Navbar>

      <div className="to-bill">
        {/* Hero: the one number that matters, with a paid-progress meter and the
            charge breakdown folded underneath. Standard outline card surface. */}
        <Card outline className={`to-bill-hero${settled ? ' to-bill-hero--settled' : ''}`}>
          <CardContent>
            <div className="to-bill-hero__label">
              {settled ? (
                <>
                  <i className="f7-icons">checkmark_seal_fill</i> {t.paidInFull}
                </>
              ) : (
                t.balanceDue
              )}
            </div>
            <div className="to-bill-hero__amount">
              {formatMoney(settled ? session.totalCents : balance, currency)}
            </div>

            {/* Receipt - reads top-down to the headline: Total − Paid = Balance.
                Tips are NOT here; they sit on top of the bill (shown per payment
                below), so including them would make the total look wrong. */}
            {showReceipt && (
              <dl className="to-bill-hero__break">
                <div>
                  <dt>{t.subtotal}</dt>
                  <dd>{formatMoney(session.subtotalCents, currency)}</dd>
                </div>
                {session.serviceChargeCents > 0 && (
                  <div>
                    <dt>{t.serviceCharge}</dt>
                    <dd>{formatMoney(session.serviceChargeCents, currency)}</dd>
                  </div>
                )}
                <div className="to-bill-hero__total">
                  <dt>{tInspector.total}</dt>
                  <dd>{formatMoney(session.totalCents, currency)}</dd>
                </div>
                {session.paidCents > 0 && (
                  <div>
                    <dt>{tInspector.paid}</dt>
                    <dd>−{formatMoney(session.paidCents, currency)}</dd>
                  </div>
                )}
              </dl>
            )}
          </CardContent>
        </Card>

        {/* Kitchen gate - the meal must be finished before any money is taken.
            The settle actions are replaced (not just disabled) by a clear,
            actionable explanation that sends staff back to the tab to serve or
            cancel the open rounds. The backend enforces the same rule. */}
        {!closed && !settled && kitchenBusy && (
          <Block style={{ paddingTop: 8 }}>
            <EmptyState
              icon="flame"
              title={t.finishMeal}
              description={t.finishMealDesc(openRoundsText)}
            />
            <Button large round fill className="margin-top" onClick={back}>
              {t.backToTab}
            </Button>
          </Block>
        )}

        {/* Settle - one forward action for where this tab is. Each opens a
            focused page; the overview itself stays read-and-decide. Hidden once
            settled: paying already closed the tab, so there's nothing to do here
            (an unclosed-but-settled edge is handled from the Manage menu). */}
        {!closed && !settled && !kitchenBusy && (
          <>
            <BlockTitle>{t.settle}</BlockTitle>
            <List dividers strongIos insetIos>
              {collectingSplit && (
                <ListItem
                  link="#"
                  title={t.collectPayments}
                  footer={t.collectPaymentsFooter(
                    collectedCount,
                    session.payments.length,
                    formatMoney(balance, currency),
                  )}
                  onClick={() => go('collect')}
                >
                  <i slot="media" className="f7-icons to-bill-nav-ic">
                    money_dollar_circle
                  </i>
                </ListItem>
              )}
              {unsplitPartial && (
                <ListItem
                  link="#"
                  title={t.takePayment}
                  footer={t.takePaymentFooter(formatMoney(balance, currency))}
                  onClick={() => go('payment')}
                >
                  <i slot="media" className="f7-icons to-bill-nav-ic">
                    creditcard
                  </i>
                </ListItem>
              )}
              {fresh && (
                <ListItem
                  link="#"
                  title={t.splitBill}
                  footer={
                    session.proposedSplit
                      ? t.guestSuggested(
                          SPLIT_LABEL[session.proposedSplit.mode] ?? session.proposedSplit.mode,
                        )
                      : session.seats.length > 0
                        ? t.bySeatFooter(session.seats.length)
                        : t.splitOptions
                  }
                  onClick={() => go('split')}
                >
                  <i slot="media" className="f7-icons to-bill-nav-ic">
                    person_2_square_stack
                  </i>
                </ListItem>
              )}
              {fresh && (
                <ListItem
                  link="#"
                  title={t.takeFullPayment}
                  footer={t.takeFullPaymentFooter(formatMoney(balance, currency))}
                  onClick={() => go('payment')}
                >
                  <i slot="media" className="f7-icons to-bill-nav-ic">
                    creditcard
                  </i>
                </ListItem>
              )}
            </List>
          </>
        )}

        {/* Payments taken - a read-only record. The collect page owns the
            actionable share list while a split is being collected; here we just
            show what's settled (an unsplit payment, or the closed tab's tally). */}
        {!collectingSplit && paidHistory.length > 0 && (
          <>
            <BlockTitle>{t.paymentsTaken}</BlockTitle>
            <List dividers strongIos insetIos>
              {paidHistory.map((p) => (
                <ListItem
                  key={p.id}
                  title={methodName(p.methodKind)}
                  footer={
                    p.tipCents > 0
                      ? t.paymentTipFooter(
                          PAY_LABEL[p.status] ?? p.status,
                          formatMoney(p.tipCents, currency),
                        )
                      : (PAY_LABEL[p.status] ?? p.status)
                  }
                  after={formatMoney(p.amountCents, currency)}
                >
                  <i slot="media" className="f7-icons to-bill-pay-ic" style={{ fontSize: 24 }}>
                    {p.methodKind === 'CASH' ? 'money_dollar_circle_fill' : 'creditcard_fill'}
                  </i>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {closed && (
          <Block style={{ paddingTop: 8 }}>
            <EmptyState
              icon="checkmark_seal_fill"
              title={t.tabClosed}
              description={t.tabClosedDesc}
            />
          </Block>
        )}
        <div style={{ height: 'calc(16px + var(--f7-safe-area-bottom, 0px))' }} />
      </div>
    </Page>
  );
}
